/**
 * MarketDataContext — Global real-time market data via DUAL channels
 *
 * Data flow (PRIORITY ORDER):
 *   1. SSE (primary, ~0ms latency):  VPS → CF Tunnel → useSSE → MarketDataContext
 *   2. WS (backup, ~1-2s latency):   VPS → CF DO → WebSocket → MarketDataContext
 *
 * FIX: Previously, only the slower WS path was used for price display.
 * SSE market_data events were received by useSSE but never consumed.
 * Now, SSE ticks are merged as the primary source, with WS enriching
 * synthetics (ATM, straddle, synthetic spot) from the Durable Object.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { useSSE } from '../hooks/useSSE'

export interface MarketTick {
  token: string           // e.g. "101999972"
  name: string            // e.g. "Nifty Bank"
  ltp: number             // last traded price
  open: number
  high: number
  low: number
  close: number           // previous close
  change: number          // absolute change from prev close
  pChange: number         // % change
  bid: number
  ask: number
  bidQty: number
  askQty: number
  volume: number
  oi: number              // open interest (0 for indices)
  atp: number             // average traded price
  type: 'index' | 'marketPicture' | 'ltpinfo' | 'unknown'
  exchange: string
  updatedAt: number       // Date.now() or VPS-side ts
  ltt?: string | null      // Greeksoft exchange last traded time e.g. "09-07-2026 12:30:15"
  // level2 market depth (marketPicture only)
  depth?: Array<{ bid: { price: number; qty: number; no: number }; ask: { price: number; qty: number; no: number } }>
}

interface MarketDataContextValue {
  ticks: Record<string, MarketTick>        // token → latest tick
  connected: boolean                        // TRUE if either SSE or WS is delivering data
  sseConnected: boolean                     // SSE direct connection status
  wsConnected: boolean                      // CF DO WebSocket connection status
  isStale: boolean                          // TRUE if no tick received for >10 seconds
  lastTickAt: number                        // timestamp of last tick from any source
  tickCount: number                         // total ticks received
  dataSource: 'sse' | 'ws' | 'none'        // which channel delivered the last tick
  subscribe: (tokens: string[], type?: 'index' | 'marketPicture' | 'ltpinfo') => Promise<void>
  unsubscribe: (tokens: string[], type?: string) => Promise<void>
}

const MarketDataContext = createContext<MarketDataContextValue>({
  ticks: {},
  connected: false,
  sseConnected: false,
  wsConnected: false,
  isStale: false,
  lastTickAt: 0,
  tickCount: 0,
  dataSource: 'none',
  subscribe: async () => {},
  unsubscribe: async () => {},
})

// Map index names from the DO to Greeksoft-style numeric tokens used by MarketTicker
export const NAME_TO_TOKEN: Record<string, string> = {
  'NIFTY': '101999957',
  'BANKNIFTY': '101999972',
  'FINNIFTY': '101999923',
  'SENSEX': '100000034',
  'INDIAVIX': '999920005',
  'BANKEX': '100000035',
  'MIDCPNIFTY': '101999999',
}

// Reverse lookup: token → symbol name
const TOKEN_TO_NAME: Record<string, string> = {}
for (const [name, token] of Object.entries(NAME_TO_TOKEN)) {
  TOKEN_TO_NAME[token] = name
}

// SSE ticks may use different tokens (marketPicture tokens from Greeksoft)
// Map ALL known Greeksoft tokens to symbol names
const ALL_TOKEN_TO_NAME: Record<string, string> = {
  ...TOKEN_TO_NAME,
  // MarketPicture tokens (from server.js TOKEN_TO_SYMBOL)
  '26000': 'NIFTY',
  '26009': 'BANKNIFTY',
  '26037': 'FINNIFTY',
  '26074': 'MIDCPNIFTY',
  '1': 'SENSEX',
  '12': 'BANKEX',
  '101003918': 'INDIAVIX',
}

// Map Greeksoft raw index names to standardized symbols (from server.js INDEX_NAME_MAP)
const GREEKSOFT_NAME_MAP: Record<string, string> = {
  'Nifty 50': 'NIFTY', 'NIFTY 50': 'NIFTY', 'Nifty': 'NIFTY', 'NIFTY': 'NIFTY',
  'Nifty Bank': 'BANKNIFTY', 'NIFTY BANK': 'BANKNIFTY', 'Bank Nifty': 'BANKNIFTY', 'BANKNIFTY': 'BANKNIFTY',
  'Nifty Fin Service': 'FINNIFTY', 'NIFTY FIN SERVICE': 'FINNIFTY', 'Fin Nifty': 'FINNIFTY', 'FINNIFTY': 'FINNIFTY',
  'Nifty Midcap Select': 'MIDCPNIFTY', 'NIFTY MIDCAP SELECT': 'MIDCPNIFTY', 'Midcap': 'MIDCPNIFTY', 'MIDCPNIFTY': 'MIDCPNIFTY',
  'India VIX': 'INDIAVIX', 'INDIA VIX': 'INDIAVIX', 'INDIAVIX': 'INDIAVIX',
  'S&P BSE SENSEX': 'SENSEX', 'SENSEX': 'SENSEX', 'Sensex': 'SENSEX',
  'S&P BSE BANKEX': 'BANKEX', 'BANKEX': 'BANKEX', 'Bankex': 'BANKEX',
  'NIFTY MIDCAP 50': 'MIDCPNIFTY', 'Nifty Midcap': 'MIDCPNIFTY',
}

// Staleness threshold: mark data as stale after 10 seconds without updates
const STALE_THRESHOLD_MS = 10_000

function synthToTicks(synthetics: Record<string, any>): Record<string, MarketTick> {
  const result: Record<string, MarketTick> = {}
  for (const [key, data] of Object.entries(synthetics)) {
    const token = NAME_TO_TOKEN[key]
    if (!token) continue
    result[token] = {
      token,
      name: key,
      ltp: data.spot || 0,
      open: 0,
      high: 0,
      low: 0,
      close: data.prevClose || 0,
      change: data.change || 0,
      pChange: data.changePct || 0,
      bid: 0,
      ask: 0,
      bidQty: 0,
      askQty: 0,
      volume: 0,
      oi: 0,
      atp: 0,
      type: 'index',
      exchange: key === 'SENSEX' || key === 'BANKEX' ? 'BSE' : 'NSE',
      updatedAt: data.ts || Date.now(),
      ltt: data.ltt || null,
      // Enriched fields from VPS (for LiveWatch / Reports)
      baseAtm: data.baseAtm,
      cePremium: data.cePremium,
      pePremium: data.pePremium,
      straddlePrice: data.straddlePrice,
      syntheticSpot: data.syntheticSpot,
      syntheticAtm: data.syntheticAtm,
    } as any
  }
  return result
}

/**
 * Convert a raw SSE market_data event to a partial MarketTick update.
 * SSE delivers raw Greeksoft Apollo ticks with ~0ms latency (direct from VPS).
 */
function sseTickToPartial(raw: any): { token: string; update: Partial<MarketTick> } | null {
  if (!raw) return null

  // Resolve symbol name from raw tick data
  const rawToken = String(raw.indexCode || raw.token || raw.ExchangeInstrumentID || '')
  const indexName = raw.name || raw.symbol || raw.indexName || ''

  // Try name-based resolution first (more reliable for index ticks)
  let symbolName = GREEKSOFT_NAME_MAP[indexName] || GREEKSOFT_NAME_MAP[indexName?.toUpperCase?.()]
  // Fallback to token-based resolution
  if (!symbolName && rawToken) {
    symbolName = ALL_TOKEN_TO_NAME[rawToken]
  }
  if (!symbolName) return null

  // Get the canonical UI token for this symbol
  const canonicalToken = NAME_TO_TOKEN[symbolName]
  if (!canonicalToken) return null

  // Extract price fields — handle both index and marketPicture formats
  const touchline = raw.Touchline || {}
  const ltp = parseFloat(raw.ltp || touchline.LastTradedPrice || touchline.LTP || raw.IndexValue || raw.spot || 0)
  if (!ltp || ltp === 0) return null

  const change = parseFloat(raw.change || touchline.NetChange || touchline.Change || 0)
  const pChange = parseFloat(raw.p_change || raw.changePct || touchline.PercentChange || touchline.PctChange || 0)
  const prevClose = parseFloat(raw.prevClose || touchline.PreviousClose || 0)

  return {
    token: canonicalToken,
    update: {
      token: canonicalToken,
      name: symbolName,
      ltp,
      change: change || 0,
      pChange: pChange || 0,
      close: prevClose || 0,
      exchange: (symbolName === 'SENSEX' || symbolName === 'BANKEX') ? 'BSE' : 'NSE',
      type: 'index',
      updatedAt: raw.ts || Date.now(),
      ltt: raw.ltt || raw.lut || null,
      // Carry enriched fields from SSE if VPS sends them
      ...(raw.baseAtm !== undefined && { baseAtm: raw.baseAtm }),
      ...(raw.cePremium !== undefined && { cePremium: raw.cePremium }),
      ...(raw.pePremium !== undefined && { pePremium: raw.pePremium }),
      ...(raw.straddlePrice !== undefined && { straddlePrice: raw.straddlePrice }),
      ...(raw.syntheticSpot !== undefined && { syntheticSpot: raw.syntheticSpot }),
      ...(raw.syntheticAtm !== undefined && { syntheticAtm: raw.syntheticAtm }),
    },
  }
}

export function MarketDataProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth()
  const sse = useSSE()
  const [ticks, setTicks] = useState<Record<string, MarketTick>>({})
  const [wsConnected, setWsConnected] = useState(false)
  const [tickCount, setTickCount] = useState(0)
  const [lastTickAt, setLastTickAt] = useState(0)
  const [isStale, setIsStale] = useState(false)
  const [dataSource, setDataSource] = useState<'sse' | 'ws' | 'none'>('none')
  const wsRef = useRef<WebSocket | null>(null)
  const retryCountRef = useRef(0) // for exponential backoff

  // ── Channel 1: SSE market_data events (PRIMARY — ~0ms latency) ──
  // Watch SSE lastEvent and merge raw Greeksoft ticks into our state.
  // This is the core fix: previously, SSE market_data was received but never used.
  const lastSseEventRef = useRef<any>(null)
  useEffect(() => {
    const event = sse.lastEvent
    if (!event || event === lastSseEventRef.current) return
    lastSseEventRef.current = event

    if (event.type !== 'market_data') return

    const result = sseTickToPartial(event.data)
    if (!result) return

    const { token: canonicalToken, update } = result
    setTicks(prev => ({
      ...prev,
      [canonicalToken]: {
        // Preserve existing enriched fields (synthetics from WS channel)
        ...(prev[canonicalToken] || {} as MarketTick),
        // Apply SSE update (ltp, change, pChange, timestamps)
        ...update,
      } as MarketTick,
    }))
    setTickCount(c => c + 1)
    setLastTickAt(Date.now())
    setDataSource('sse')
    setIsStale(false)
  }, [sse.lastEvent])

  // ── Channel 2: CF Durable Object WebSocket (BACKUP — ~1-2s latency) ──
  // Enriches ticks with synthetics (ATM, straddle, synthetic spot) computed by the DO.
  // Also provides initial state and catchup data.
  useEffect(() => {
    let reconnectTimeout: ReturnType<typeof setTimeout>
    let isCancelled = false

    const connectWS = () => {
      if (isCancelled) return
      try {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const apiHost = import.meta.env.VITE_API_URL
          ? new URL(import.meta.env.VITE_API_URL).host
          : window.location.host

        const ws = new WebSocket(`${wsProtocol}//${apiHost}/api/marketdata/ws`)
        wsRef.current = ws

        ws.onopen = () => {
          setWsConnected(true)
          retryCountRef.current = 0 // reset backoff on successful connect
        }

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data)
            if (msg.type === 'initial_state' || msg.type === 'synthetics_update') {
              const synthetics = msg.synthetics || msg.data
              if (synthetics) {
                const newTicks = synthToTicks(synthetics)
                setTicks(prev => {
                  const merged = { ...prev }
                  for (const [tk, wsTick] of Object.entries(newTicks)) {
                    // Only update from WS if SSE hasn't provided a fresher tick
                    const existing = merged[tk]
                    if (existing && existing.updatedAt > (wsTick.updatedAt || 0)) {
                      // SSE data is fresher — only merge synthetics from WS
                      merged[tk] = {
                        ...existing,
                        // Enrich with synthetics from DO (these are computed server-side)
                        baseAtm: (wsTick as any).baseAtm ?? (existing as any).baseAtm,
                        cePremium: (wsTick as any).cePremium ?? (existing as any).cePremium,
                        pePremium: (wsTick as any).pePremium ?? (existing as any).pePremium,
                        straddlePrice: (wsTick as any).straddlePrice ?? (existing as any).straddlePrice,
                        syntheticSpot: (wsTick as any).syntheticSpot ?? (existing as any).syntheticSpot,
                        syntheticAtm: (wsTick as any).syntheticAtm ?? (existing as any).syntheticAtm,
                      } as any
                    } else {
                      // No SSE data yet or WS is fresher — use WS tick entirely
                      merged[tk] = wsTick
                    }
                  }
                  return merged
                })
                setTickCount(c => c + 1)
                const now = Date.now()
                setLastTickAt(prev => prev || now) // don't override if SSE already set it
                if (dataSource === 'none') setDataSource('ws')
              }
            }
          } catch (e) {
            console.error('[MarketData] WS parse error', e)
          }
        }

        ws.onerror = () => {
          setWsConnected(false)
        }

        ws.onclose = () => {
          setWsConnected(false)
          wsRef.current = null
          if (!isCancelled) {
            // FIX: Exponential backoff (was flat 3s) — 1s, 2s, 4s, 8s, max 30s
            const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30000)
            retryCountRef.current++
            console.log(`[MarketData] WS closed — reconnecting in ${delay / 1000}s (attempt ${retryCountRef.current})`)
            reconnectTimeout = setTimeout(connectWS, delay)
          }
        }
      } catch (err) {
        console.error('[MarketData] WS connection error', err)
      }
    }

    connectWS()

    return () => {
      isCancelled = true
      clearTimeout(reconnectTimeout)
      if (wsRef.current) wsRef.current.close()
    }
  }, [])

  // ── Staleness detector — marks data as stale after STALE_THRESHOLD_MS ──
  useEffect(() => {
    const interval = setInterval(() => {
      if (lastTickAt > 0 && Date.now() - lastTickAt > STALE_THRESHOLD_MS) {
        setIsStale(true)
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [lastTickAt])

  // subscribe / unsubscribe are no-ops for now since DO handles everything
  const subscribe = useCallback(async () => {}, [])
  const unsubscribe = useCallback(async () => {}, [])

  // Unified connection status: true if EITHER channel is delivering data
  const connected = sse.connected || wsConnected

  return (
    <MarketDataContext.Provider value={{
      ticks,
      connected,
      sseConnected: sse.connected,
      wsConnected,
      isStale,
      lastTickAt,
      tickCount,
      dataSource,
      subscribe,
      unsubscribe,
    }}>
      {children}
    </MarketDataContext.Provider>
  )
}

export function useMarketData() {
  return useContext(MarketDataContext)
}

// Convenience hook for a single token's tick
export function useTick(token: string): MarketTick | null {
  const { ticks } = useMarketData()
  return ticks[token] ?? null
}
